// Phases 5-6: generating the per-chunk markdown files and assembling the
// unified-branches.json from the parsed sections and plan endings.
//
// Branchpoint blocks are intentionally NOT extracted into their own graph
// nodes anymore — they render in place as inline expandable boxes via the
// runtime `:::branchpoint` directive handler. The `Branchpoint` /
// `branchpoints` parameters threaded through here are kept for signature
// stability with the orchestrator but are expected to be empty.
import type { UnifiedBranchesData, BranchNode, BranchNodeSection } from '../../../lib/branchTypes'
import { injectMainScenarioTabs } from './directives'
import {
  slugify,
  trimContent,
  escapeRegex,
  repairBranchpointFences,
  type Section,
  type SectionMeta,
  type Branchpoint,
  type PlanEnding,
} from './parsing'

export const MD_PREFIX = 'generated/branching-markdown-v7'
const FOOTNOTES_FILENAME = 'plan-a--footnotes.md'

// Result of phase 5: the chunk files plus the metadata phase 6 needs.
export type GeneratedChunks = {
  chunkContents: Record<string, string>
  rootMd: string
  section2026Title: string
  section2026Slug: string
  section2026Filename: string
  planASectionMeta: SectionMeta[]
}

// Strips a leading "Please note:" preamble paragraph from the root content.
function stripPleaseNotePreamble(rootContent: string): string {
  const firstParagraphMatch = rootContent.match(/^\s*([\s\S]*?)(?:\n\s*\n|$)/)
  if (firstParagraphMatch && /Please note:/i.test(firstParagraphMatch[1])) {
    return rootContent.slice(firstParagraphMatch[0].length)
  }
  return rootContent
}

// Builds the root chunk file and returns its cleaned markdown.
function generateRootChunk(rootContent: string, chunkContents: Record<string, string>): string {
  const rootMd = trimContent(repairBranchpointFences(stripPleaseNotePreamble(rootContent)))
  chunkContents[`${MD_PREFIX}/the-playbook-root.md`] = rootMd
  return rootMd
}

// Builds the 2026-2028 chunk file and returns its derived title/slug/filename.
// Runs `repairBranchpointFences` so any `:::branchpoint` blocks authored in
// this region (where Plan A's section-splitter never reaches) parse correctly
// at runtime when their fence length matches a nested `:::branchSummary`.
// `section2026Tail` (the hoisted 2029 dateMarker + H1, when the doc places
// them above the plan-ending blocks) is appended AFTER Scenario-tab injection
// so it can't be captured into the last perspectiveBox's Scenario tab.
function generate2026Chunk(
  section2026_2028: string,
  chunkContents: Record<string, string>,
  section2026Tail?: string,
): { title: string; slug: string; filename: string } {
  const repaired = repairBranchpointFences(section2026_2028)
  const section2026H1 = repaired.match(/^# (.+)$/m)
  const title = section2026H1 ? section2026H1[1].trim() : '2026 - 2028'
  const slug = slugify(title)
  const filename = `${slug}.md`
  const body = trimContent(injectMainScenarioTabs(repaired))
  chunkContents[`${MD_PREFIX}/${filename}`] = section2026Tail
    ? body + '\n' + trimContent(section2026Tail)
    : body
  return { title, slug, filename }
}

// Builds one Plan A section chunk (stripping the epilogue's own H1, or the
// hoisted 2029 H1 that already renders above the flowchart at the end of the
// 2026-2028 chunk) and returns its metadata entry.
function generatePlanASectionChunk(
  section: Section,
  chunkContents: Record<string, string>,
  hoistedSectionTitle?: string,
): SectionMeta {
  const slug = slugify(section.title)
  const id = `plan-a--${slug}`
  const filename = `${id}.md`
  const epilogueTitle = /^Epilogue\b/i.test(section.title) ? section.title : undefined
  const stripTitle = epilogueTitle ?? (section.title === hoistedSectionTitle ? section.title : undefined)
  const sectionContentRaw = stripTitle
    ? section.content.replace(new RegExp(`^# ${escapeRegex(stripTitle)}\\s*$\\n*`, 'm'), '')
    : section.content
  chunkContents[`${MD_PREFIX}/${filename}`] = trimContent(injectMainScenarioTabs(sectionContentRaw))
  return { id, title: section.title, filename, epilogueTitle, summary: section.summary }
}

// Builds one plan-ending chunk, prefixing the shared 2029 dateMarker header.
function generatePlanEndingChunk(ending: PlanEnding, chunkContents: Record<string, string>): void {
  const nodeId = `plan-${ending.planLetter.toLowerCase()}-root`
  const dateMarkerHeader = ':::dateMarker\n2029-01-01\n2029\n:::\n\n'
  chunkContents[`${MD_PREFIX}/${nodeId}.md`] = dateMarkerHeader + trimContent(ending.content)
}

// Phase 5: generate every chunk markdown file in `chunkContents`.
export function generateChunkFiles(
  rootContent: string,
  section2026_2028: string,
  planASections: Section[],
  _branchpoints: Branchpoint[],
  planEndings: PlanEnding[],
  footnotesContent: string,
  hoistedSectionTitle?: string,
  section2026Tail?: string,
): GeneratedChunks {
  const chunkContents: Record<string, string> = {}
  const rootMd = generateRootChunk(rootContent, chunkContents)
  const s2026 = generate2026Chunk(section2026_2028, chunkContents, section2026Tail)
  const planASectionMeta = planASections.map(s => generatePlanASectionChunk(s, chunkContents, hoistedSectionTitle))
  for (const ending of planEndings) generatePlanEndingChunk(ending, chunkContents)
  if (footnotesContent) {
    chunkContents[`${MD_PREFIX}/${FOOTNOTES_FILENAME}`] = trimContent(footnotesContent)
  }
  return {
    chunkContents,
    rootMd,
    section2026Title: s2026.title,
    section2026Slug: s2026.slug,
    section2026Filename: s2026.filename,
    planASectionMeta,
  }
}

type PlanASegment = {
  nodeId: string
  sections: SectionMeta[]
  summary?: string
}

// Returns true when the node's identity comes from the v7 pipeline — it
// either anchors a v7 chunk itself or its sections do, or it's one of the
// well-known v7 segment / stub node id patterns from prior runs (those nodes
// may have had their `markdownFile` cleared, but their id alone is enough to
// recognise them as ours to manage).
function isV7PipelineNode(nodeId: string, node: BranchNode): boolean {
  if (nodeId === 'plan-a-root' || /^plan-a-cont-\d+$/.test(nodeId)) return true
  const refs = [
    node.markdownFile,
    ...(node.sections?.map(s => s.markdownFile) ?? []),
  ]
  return refs.some(f => !!f && f.startsWith(`${MD_PREFIX}/`))
}

// Drops every v7-pipeline node from the previous run that this run won't
// rewrite. `liveNodeIds` is the set the assembler is about to write — anything
// outside it (e.g. orphan `plan-a-cont-*` segments now that section-splitting
// is gone, or stub nodes from before branchpoints rendered inline) is pruned.
// Hand-curated nodes (plan-{b,c,d,s}-root, floating-*, the-playbook-root,
// etc.) aren't v7 pipeline nodes, so they're left alone.
function pruneStaleNodes(
  unifiedJson: UnifiedBranchesData,
  liveNodeIds: Set<string>,
): void {
  for (const [nodeId, node] of Object.entries(unifiedJson.nodes)) {
    if (liveNodeIds.has(nodeId)) continue
    if (!isV7PipelineNode(nodeId, node as BranchNode)) continue
    delete unifiedJson.nodes[nodeId]
  }
}

// Writes the playbook-root node (with its single 2026-2028 sub-section).
function writePlaybookRootNode(unifiedJson: UnifiedBranchesData, chunks: GeneratedChunks): void {
  unifiedJson.nodes['the-playbook-root'] = {
    ...unifiedJson.nodes['the-playbook-root'],
    markdownFile: `${MD_PREFIX}/the-playbook-root.md`,
    sections: [{
      id: chunks.section2026Slug,
      title: chunks.section2026Title,
      markdownFile: `${MD_PREFIX}/${chunks.section2026Filename}`,
    }],
  } as BranchNode
}

// All Plan A sections collapse into a single `plan-a-root` segment now that
// branchpoints no longer split the timeline into continuation nodes. Kept as
// a function so the data shape parallels the plan-{b,c,d,s}-root entries.
function buildPlanASegments(planASectionMeta: SectionMeta[]): PlanASegment[] {
  if (planASectionMeta.length === 0) return []
  const summary = planASectionMeta[0].summary
  return [{ nodeId: 'plan-a-root', sections: planASectionMeta, summary }]
}

const sectionsForJson = (sections: SectionMeta[]): BranchNodeSection[] => sections.map(s => ({
  id: s.id,
  title: s.title,
  markdownFile: `${MD_PREFIX}/${s.filename}`,
  epilogueTitle: s.epilogueTitle,
}))

// Writes one Plan A segment node, merging existing fields.
function writePlanASegmentNode(
  unifiedJson: UnifiedBranchesData,
  segment: PlanASegment,
): void {
  const existingNode: BranchNode = (unifiedJson.nodes[segment.nodeId] as BranchNode) || ({} as BranchNode)
  const { markdownFile: _stripMd, ...existingNodeWithoutMd } = existingNode
  void _stripMd
  unifiedJson.nodes[segment.nodeId] = {
    ...existingNodeWithoutMd,
    id: segment.nodeId,
    title: existingNodeWithoutMd.title || (segment.nodeId === 'plan-a-root' ? 'PLAN A' : 'Plan A continues'),
    ...(segment.nodeId === 'plan-a-root' ? { primary: true, planId: 'A' as const } : { primary: existingNodeWithoutMd.primary !== undefined ? existingNodeWithoutMd.primary : true }),
    sections: sectionsForJson(segment.sections),
    branchPoints: undefined,
    ...(segment.summary !== undefined ? { summary: segment.summary } : {}),
  } as BranchNode
}

// Points an existing plan-ending node at its regenerated chunk file.
function linkPlanEndingNode(unifiedJson: UnifiedBranchesData, ending: PlanEnding): void {
  const nodeId = `plan-${ending.planLetter.toLowerCase()}-root`
  if (unifiedJson.nodes[nodeId]) {
    unifiedJson.nodes[nodeId] = {
      ...unifiedJson.nodes[nodeId],
      markdownFile: `${MD_PREFIX}/${nodeId}.md`,
    } as BranchNode
  }
}

type WithFootnotes = BranchNode & { footnotesFile?: string }

// Attaches the shared footnotes file ref to every Plan A-family node.
function attachFootnotesRef(
  unifiedJson: UnifiedBranchesData,
  planASegments: PlanASegment[],
  planEndings: PlanEnding[],
): void {
  const footnotesFileRef = `${MD_PREFIX}/${FOOTNOTES_FILENAME}`
  ;(unifiedJson.nodes['the-playbook-root'] as WithFootnotes).footnotesFile = footnotesFileRef
  const setRef = (nodeId: string): void => {
    const node = unifiedJson.nodes[nodeId] as WithFootnotes | undefined
    if (node) node.footnotesFile = footnotesFileRef
  }
  for (const segment of planASegments) setRef(segment.nodeId)
  for (const ending of planEndings) setRef(`plan-${ending.planLetter.toLowerCase()}-root`)
}

// Phase 6: assemble the unified-branches.json (mutates `unifiedJson`).
export function assembleUnifiedJson(
  unifiedJson: UnifiedBranchesData,
  chunks: GeneratedChunks,
  _branchpoints: Branchpoint[],
  planEndings: PlanEnding[],
  footnotesContent: string,
): void {
  const planASegments = buildPlanASegments(chunks.planASectionMeta)
  const liveNodeIds = new Set<string>([
    'the-playbook-root',
    ...planASegments.map(s => s.nodeId),
    ...planEndings.map(e => `plan-${e.planLetter.toLowerCase()}-root`),
  ])
  pruneStaleNodes(unifiedJson, liveNodeIds)
  writePlaybookRootNode(unifiedJson, chunks)
  for (const segment of planASegments) {
    writePlanASegmentNode(unifiedJson, segment)
  }
  for (const ending of planEndings) linkPlanEndingNode(unifiedJson, ending)
  if (footnotesContent) attachFootnotesRef(unifiedJson, planASegments, planEndings)
  // No generatedAt timestamp: it guaranteed a merge conflict whenever two
  // branches each ran an import, for a value nothing reads. The delete
  // migrates older committed JSONs that still carry the field.
  delete (unifiedJson as { generatedAt?: string }).generatedAt
}
